import { ReactElement } from "react";

import { BulkRecord, dataColumns, reasonsOf } from "../types";
import { cellText, columnLabel, exportRecords } from "../export-records";

interface BulkRecordsTableProps {
  title: string;
  records: BulkRecord[];
  tone: "ok" | "bad";
  /** Renders each row's exclusion reasons in a dedicated final column. */
  showReasons?: boolean;
  emptyMessage: string;
  /** Adds an Export action writing these rows to a workbook. */
  exportFileName?: string;
}

/**
 * Renders whatever the backend returned.
 *
 * The record shape is the backend's and is not pinned down here, so columns
 * are derived from the rows themselves. That way a field added server-side
 * shows up without a frontend change, and nothing is silently dropped.
 */
const BulkRecordsTable: React.FC<BulkRecordsTableProps> = ({
  title,
  records,
  tone,
  showReasons = false,
  emptyMessage,
  exportFileName,
}): ReactElement => {
  const columns = dataColumns(records);

  return (
    <section className="records">
      <header className={tone}>
        <h5>{title}</h5>
        <span className="count">{records.length.toLocaleString("en-US")}</span>
        {exportFileName && records.length > 0 && (
          <button
            type="button"
            className="records__export"
            onClick={() =>
              exportRecords(records, showReasons, title, exportFileName)
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v11" />
              <path d="m8 10.5 4 4 4-4" />
              <path d="M4.5 20.5h15" />
            </svg>
            Export
          </button>
        )}
      </header>

      {records.length === 0 ? (
        <p className="empty">{emptyMessage}</p>
      ) : (
        <div className="scroll">
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>{columnLabel(column)}</th>
                ))}
                {showReasons && <th>Exclusion reasons</th>}
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => {
                const reasons = reasonsOf(record);
                return (
                  <tr key={index}>
                    {columns.map((column) => {
                      const value = cellText(record[column]);
                      return (
                        <td
                          key={column}
                          className={value ? "" : "is-blank"}
                          title={value}
                        >
                          {value || "—"}
                        </td>
                      );
                    })}
                    {showReasons && (
                      <td>
                        {reasons.length > 0 ? (
                          <div className="reasons">
                            {reasons.map((reason, i) => (
                              <span key={i}>{reason}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="is-blank">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default BulkRecordsTable;
